import { nameValidationRegex } from '@/constants/regex';
import { isGoogleMapsUrl } from '@/lib/isGoogleMapsUrl';
import { optionalUploadValueSchema } from '@/lib/uploadSchema';
import { z } from 'zod';

const currentYear = new Date().getFullYear();

// ✅ Keep the raw object shape separate so .extend() remains available
const hostFormBaseSchema = z.object({
  position: z
    .string()
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    })
    .optional(),
  hostName: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  furigana: z
    .string()
    .trim()
    .min(1, 'common.form.validation.required')
    .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
  dobYear: z
    .string()
    .regex(/^(|19\d{2}|20\d{2})$/, 'common.form.validation.invalid')
    .refine((val) => val === '' || Number(val) <= currentYear, 'common.form.validation.invalid')
    .optional(),
  dobMonth: z
    .string()
    .regex(/^(?:|0?[1-9]|1[0-2])$/, 'common.form.validation.invalid')
    .optional(),
  dobDay: z
    .string()
    .regex(/^(?:|0?[1-9]|[12][0-9]|3[01])$/, 'common.form.validation.invalid')
    .optional(),
  rank: z.string().optional().or(z.literal('')),
  birthPlace: z.string().optional(),
  bloodType: z.string().optional(),
  constellation: z.string().optional(),
  mbti: z
    .string()
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    })
    .optional(),
  title: z
    .string()
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    })
    .optional(),
  height: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value) return true;

        const num = Number(value);

        return !Number.isNaN(num) && num >= 135 && num <= 220;
      },
      {
        message: '身長は135以上220以内にしてください',
      },
    ),
  instagram: z.string().url('common.form.validation.enterValidUrl').optional().or(z.literal('')),
  tiktok: z.string().url('common.form.validation.enterValidUrl').optional().or(z.literal('')),
  line: z.string().url('common.form.validation.enterValidUrl').optional().or(z.literal('')),
  twitter: z.string().url('common.form.validation.enterValidUrl').optional().or(z.literal('')),
  website: z.string().url('common.form.validation.enterValidUrl').optional().or(z.literal('')),
  googleMap: z.string().refine(isGoogleMapsUrl, 'common.form.validation.enterValidUrl').optional().or(z.literal('')),
  skills_appearance: z.string().optional().or(z.literal('')),
  skills_conversationStyle: z.string().optional().or(z.literal('')),
  skills_alcoholTolerance: z.string().optional().or(z.literal('')),
  skills_atmosphere: z.string().optional().or(z.literal('')),
  profileImage: optionalUploadValueSchema,
});

const withAgeValidation = <T extends z.ZodObject<z.ZodRawShape>>(schema: T) =>
  schema.superRefine((data, ctx) => {
    const { dobYear, dobMonth, dobDay } = data as {
      dobYear?: string;
      dobMonth?: string;
      dobDay?: string;
    };

    if (!dobYear || dobYear === '') return;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-indexed
    const currentDay = today.getDate();

    const year = Number(dobYear);
    const month = dobMonth && dobMonth !== '' ? Number(dobMonth) : null;
    const day = dobDay && dobDay !== '' ? Number(dobDay) : null;

    const turningAge = currentYear - year;

    // ── Year check ───────────────────────────────────────────────
    // If turning less than 18 this year → year alone is invalid
    // If turning more than 18 this year → all good, stop
    // If turning exactly 18 this year → need to check month/day
    if (turningAge < 18) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.ageValidation',
        path: ['dobYear'],
      });
      return; // no need to check month/day
    }

    if (turningAge > 18) return; // clearly 18+, skip further checks

    // ── turningAge === 18: check month ───────────────────────────
    if (month === null) return; // month not filled, give benefit of doubt

    if (month > currentMonth) {
      // Birthday month is still ahead this year → under 18
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.ageValidation',
        path: ['dobMonth'],
      });
      return;
    }

    if (month < currentMonth) return; // birthday month already passed → 18+

    // ── month === currentMonth: check day ────────────────────────
    if (day === null) return; // day not filled, give benefit of doubt

    if (day > currentDay) {
      // Birthday is still ahead this month → under 18
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.ageValidation',
        path: ['dobDay'],
      });
    }
    // day <= currentDay → birthday already passed this month → 18+
  });
// ✅ .extend() is called on the ZodObject BEFORE superRefine
export const hostFormSchema = withAgeValidation(hostFormBaseSchema);

export const addHostFormSchema = withAgeValidation(
  hostFormBaseSchema.extend({
    profileImage: optionalUploadValueSchema,
  }),
);

export type HostFormValues = z.infer<typeof hostFormSchema>;
export type AddHostFormValues = z.infer<typeof addHostFormSchema>;
